import {
	type DefaultMessagesResponse,
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import { usePlacementApi } from '@post-office/use-placement-api';

import { DismissalFlag } from '@post-office/components--ad-controls';

import { useEffect, useState } from 'react';

import { filterExperimentalMessages } from '../common/experimental-message-list';

import {
	filterByClientEligibility,
	selectHighestPriorityMessage,
} from '../common/select-highest-priority-message';

import { type AppProps, type PlacementRenderProps } from '../common/types';

export type { PlacementRenderProps } from '../common/types';

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

const placementConfig: PlacementBrowserConfig = {
	placementId: 'townsquare-side-nav',
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
		hideInIsolatedCloud: false,
	},
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	AppProps
> = createFrontend<AppProps>((Messages) => (appProps) => {
	const [messages, setMessages] = useState<DefaultMessagesResponse['messages']>([]);
	const [dismissedInstanceId, setDismissedInstanceId] = useState<string | null>(null);
	const apiResponse = usePlacementApi<DefaultMessagesResponse>('/');

	useEffect(() => {
		const { error, data, isLoading } = apiResponse;

		if (isLoading) {
			return;
		}

		if (error) {
			throw error;
		}

		setMessages(data?.messages ?? []);
	}, [apiResponse]);

	const handleDismiss = (messageInstanceId: string) => {
		setDismissedInstanceId(messageInstanceId);
		setMessages((prevMessages) =>
			prevMessages.filter((message) => messageInstanceId !== message.messageInstanceId),
		);
	};

	// filterByClientEligibility runs first so that ineligible messages are dropped
	// before Statsig is called, preventing spurious experiment exposures.
	const eligibleMessages = filterExperimentalMessages(
		filterByClientEligibility(messages, appProps.messageEligibility),
	);
	const visibleMessages = selectHighestPriorityMessage(eligibleMessages);

	if (visibleMessages.length === 0 && !dismissedInstanceId) {
		return null;
	}

	return (
		<>
			{dismissedInstanceId && (
				<DismissalFlag
					messageInstanceId={dismissedInstanceId}
					onCloseFlag={() => setDismissedInstanceId(null)}
				/>
			)}
			{visibleMessages.map((message) => (
				<Messages.render
					key={message.messageInstanceId}
					dismiss={() => handleDismiss(message.messageInstanceId)}
					{...appProps}
					{...message}
				/>
			))}
		</>
	);
});
